import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowLeft, 
  Bell, 
  BellOff, 
  Clock, 
  Globe, 
  Pause, 
  Play,
  Sparkles,
  Check,
  ChevronRight,
  Smartphone
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({ baseURL: API });
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const PerlasDiarias = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { pearlId } = useParams();
  
  // States
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  const [subtopics, setSubtopics] = useState([]);
  const [currentPreference, setCurrentPreference] = useState(null);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [isIOS, setIsIOS] = useState(false);
  const [isPWA, setIsPWA] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [resubscribing, setResubscribing] = useState(false);
  
  // Form states
  const [selectedModule, setSelectedModule] = useState('');
  const [selectedSubtopic, setSelectedSubtopic] = useState('');
  const [intervalMinutes, setIntervalMinutes] = useState(60);
  const [startHour, setStartHour] = useState('08:00');
  const [endHour, setEndHour] = useState('22:00');
  const [timezone, setTimezone] = useState('America/Mexico_City');
  
  // Pearl detail view (for deep links)
  const [pearlDetail, setPearlDetail] = useState(null);

  useEffect(() => {
    checkPushSupport();
    loadData();
    checkSubscriptionStatus();
    
    // If pearlId in URL, load pearl detail
    if (pearlId) {
      loadPearlDetail(pearlId);
    }
  }, [pearlId]);

  useEffect(() => {
    // Load subtopics when module changes
    if (selectedModule) {
      loadSubtopics(selectedModule);
    } else {
      setSubtopics([]);
      setSelectedSubtopic('');
    }
  }, [selectedModule]);

  const checkPushSupport = () => {
    // Check if running as PWA
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
    setIsPWA(isStandalone);
    
    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);
    
    // Check push support
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setPushSupported(supported);
    
    if (supported && 'Notification' in window) {
      setPushPermission(Notification.permission);
    }
  };

  const checkSubscriptionStatus = async () => {
    try {
      const res = await apiClient.get('/perlas/subscription-status');
      setSubscriptionStatus(res.data);
      
      // If needs resubscribe, show alert
      if (res.data.needs_resubscribe) {
        console.warn('Push subscription expired - needs resubscribe');
      }
    } catch (error) {
      console.error('Error checking subscription status:', error);
    }
  };

  const handleResubscribe = async () => {
    setResubscribing(true);
    try {
      // Request permission if needed
      if (Notification.permission !== 'granted') {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          toast.error('Necesitas permitir notificaciones');
          return;
        }
      }
      
      // Resubscribe to push
      const success = await subscribeToPush();
      if (success) {
        toast.success('¡Suscripción renovada! Ahora recibirás notificaciones.');
        checkSubscriptionStatus();
      } else {
        toast.error('Error al renovar suscripción');
      }
    } catch (error) {
      console.error('Error resubscribing:', error);
      toast.error('Error al renovar suscripción');
    } finally {
      setResubscribing(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load modules
      const modulesRes = await apiClient.get('/perlas/modules');
      setModules(modulesRes.data);
      
      // Load current preference
      const prefRes = await apiClient.get('/perlas/user/preference');
      if (prefRes.data) {
        setCurrentPreference(prefRes.data);
        setSelectedModule(prefRes.data.module_id);
        setSelectedSubtopic(prefRes.data.subtopic_id);
        setIntervalMinutes(prefRes.data.interval_minutes || 60);
        setStartHour(prefRes.data.start_hour);
        setEndHour(prefRes.data.end_hour);
        setTimezone(prefRes.data.timezone);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const loadSubtopics = async (moduleId) => {
    try {
      const res = await apiClient.get(`/perlas/subtopics/${moduleId}`);
      setSubtopics(res.data);
    } catch (error) {
      console.error('Error loading subtopics:', error);
    }
  };

  const loadPearlDetail = async (id) => {
    try {
      const res = await apiClient.get(`/perlas/${id}`);
      setPearlDetail(res.data);
    } catch (error) {
      console.error('Error loading pearl:', error);
      toast.error('Perla no encontrada');
    }
  };

  const requestNotificationPermission = async () => {
    if (!pushSupported) {
      toast.error('Tu navegador no soporta notificaciones push');
      return false;
    }
    
    try {
      const permission = await Notification.requestPermission();
      setPushPermission(permission);
      
      if (permission === 'granted') {
        toast.success('¡Permisos de notificación activados!');
        return true;
      } else {
        toast.error('Necesitas permitir notificaciones para recibir perlas');
        return false;
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
      toast.error('Error al solicitar permisos');
      return false;
    }
  };

  const subscribeToPush = async () => {
    try {
      // Get VAPID public key
      const vapidRes = await apiClient.get('/perlas/vapid-public-key');
      const publicKey = vapidRes.data.publicKey;
      
      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      
      // First, unsubscribe from any existing subscription (to handle key changes)
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        await existingSubscription.unsubscribe();
        console.log('Unsubscribed from old push subscription');
      }
      
      // Subscribe to push with new keys
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });
      
      // Send subscription to server
      const subscriptionJson = subscription.toJSON();
      await apiClient.post('/perlas/push-subscription', {
        endpoint: subscriptionJson.endpoint,
        keys: subscriptionJson.keys
      });
      
      console.log('Push subscription successful');
      return true;
    } catch (error) {
      console.error('Error subscribing to push:', error);
      // Show more specific error
      if (error.message) {
        toast.error(`Error: ${error.message}`);
      }
      return false;
    }
  };

  const handleActivate = async () => {
    if (!selectedModule || !selectedSubtopic) {
      toast.error('Selecciona un módulo y subtema');
      return;
    }
    
    // Warn about very frequent notifications (every 15 min)
    if (intervalMinutes === 15) {
      const confirm = window.confirm(
        '¿Estás seguro de querer recibir una perla cada 15 minutos? Esto puede ser muy frecuente. Recomendamos empezar con cada 60 minutos.'
      );
      if (!confirm) return;
    }
    
    try {
      // Request notification permission
      const permissionGranted = await requestNotificationPermission();
      if (!permissionGranted) return;
      
      // Subscribe to push
      const pushSubscribed = await subscribeToPush();
      if (!pushSubscribed) {
        toast.error('Error al configurar notificaciones push');
        return;
      }
      
      // Save preference
      await apiClient.post('/perlas/subscribe', {
        module_id: selectedModule,
        subtopic_id: selectedSubtopic,
        interval_minutes: intervalMinutes,
        start_hour: startHour,
        end_hour: endHour,
        timezone: timezone
      });
      
      toast.success('¡Suscripción activada! Recibirás perlas diarias.');
      loadData();
      
    } catch (error) {
      console.error('Error activating:', error);
      toast.error('Error al activar suscripción');
    }
  };

  const handlePause = async () => {
    try {
      await apiClient.put('/perlas/pause');
      toast.success('Notificaciones pausadas');
      loadData();
    } catch (error) {
      toast.error('Error al pausar');
    }
  };

  const handleResume = async () => {
    try {
      await apiClient.put('/perlas/resume');
      toast.success('Notificaciones reanudadas');
      loadData();
    } catch (error) {
      toast.error('Error al reanudar');
    }
  };

  const handleUnsubscribe = async () => {
    const confirm = window.confirm('¿Seguro que quieres cancelar tu suscripción?');
    if (!confirm) return;
    
    try {
      await apiClient.delete('/perlas/unsubscribe');
      setCurrentPreference(null);
      toast.success('Suscripción cancelada');
    } catch (error) {
      toast.error('Error al cancelar');
    }
  };

  // Helper function to convert base64 to Uint8Array
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  // Pearl Detail View
  if (pearlDetail) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50">
        {/* Header */}
        <header className="bg-white shadow-sm sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center">
            <button
              onClick={() => {
                setPearlDetail(null);
                navigate('/perlas-diarias');
              }}
              className="mr-4 p-2 rounded-full hover:bg-gray-100"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-xl font-bold text-gray-800">Perla del día</h1>
          </div>
        </header>

        {/* Pearl Content */}
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            {/* Module/Subtopic badge */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
              <div className="flex items-center gap-2 text-white/80 text-sm">
                <span>{pearlDetail.module?.name}</span>
                <ChevronRight size={16} />
                <span>{pearlDetail.subtopic?.name}</span>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Sparkles className="text-amber-500" size={24} />
                {pearlDetail.pearl.title}
              </h2>
              
              <div className="bg-amber-50 rounded-xl p-4 mb-6">
                <p className="text-gray-700 text-lg leading-relaxed">
                  {pearlDetail.pearl.message}
                </p>
              </div>
              
              {pearlDetail.pearl.long_body && (
                <div className="prose prose-amber max-w-none">
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    Explicación detallada
                  </h3>
                  <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                    {pearlDetail.pearl.long_body}
                  </p>
                </div>
              )}
              
              {pearlDetail.pearl.tags?.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-2">
                  {pearlDetail.pearl.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main Subscription View
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/')}
              className="mr-4 p-2 rounded-full hover:bg-gray-100"
              data-testid="back-button"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-xl font-bold text-gray-800">Perlas Diarias</h1>
          </div>
          
          {/* Status Badge */}
          {currentPreference && (
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
              currentPreference.is_paused 
                ? 'bg-yellow-100 text-yellow-700'
                : 'bg-green-100 text-green-700'
            }`}>
              {currentPreference.is_paused ? (
                <>
                  <Pause size={14} />
                  Pausado
                </>
              ) : (
                <>
                  <Check size={14} />
                  Suscrito
                </>
              )}
            </div>
          )}
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* iOS PWA Warning */}
        {isIOS && !isPWA && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <Smartphone className="text-blue-500 flex-shrink-0 mt-1" size={20} />
              <div>
                <h3 className="font-semibold text-blue-800">
                  Para recibir notificaciones en iPhone
                </h3>
                <p className="text-blue-700 text-sm mt-1">
                  Debes agregar esta app a tu pantalla de inicio:
                </p>
                <ol className="text-blue-600 text-sm mt-2 list-decimal list-inside space-y-1">
                  <li>Toca el botón compartir <span className="inline-block w-4 h-4 align-middle">⬆️</span></li>
                  <li>Selecciona "Agregar a pantalla de inicio"</li>
                  <li>Abre la app desde tu pantalla de inicio</li>
                </ol>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Expired Warning */}
        {subscriptionStatus?.needs_resubscribe && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6" data-testid="resubscribe-alert">
            <div className="flex items-start gap-3">
              <BellOff className="text-red-500 flex-shrink-0 mt-1" size={20} />
              <div className="flex-1">
                <h3 className="font-semibold text-red-800">
                  Tu suscripción push ha expirado
                </h3>
                <p className="text-red-700 text-sm mt-1">
                  Las notificaciones no se están enviando. Esto puede pasar cuando limpias el caché del navegador o después de un tiempo sin usar la app.
                </p>
                <button
                  onClick={handleResubscribe}
                  disabled={resubscribing}
                  className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                  data-testid="resubscribe-button"
                >
                  {resubscribing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Renovando...
                    </>
                  ) : (
                    <>
                      <Bell size={16} />
                      Renovar suscripción
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
          </div>
        ) : (
          <>
            {/* Intro Section */}
            <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
                  <Sparkles className="text-white" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">
                    Aprende mientras vives
                  </h2>
                  <p className="text-gray-500 text-sm">
                    Recibe perlas médicas durante el día
                  </p>
                </div>
              </div>
              
              <p className="text-gray-600">
                Selecciona un tema de tu interés y recibe notificaciones con datos 
                clave, tips clínicos y recordatorios importantes para tu preparación ENARM.
              </p>
            </div>

            {/* Subscription Form */}
            <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Configura tu suscripción
              </h3>
              
              {/* Module Select */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Módulo
                </label>
                <select
                  value={selectedModule}
                  onChange={(e) => setSelectedModule(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  data-testid="module-select"
                >
                  <option value="">Selecciona un módulo</option>
                  {modules.map((module) => (
                    <option key={module.id} value={module.id}>
                      {module.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Subtopic Select */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subtema
                </label>
                <select
                  value={selectedSubtopic}
                  onChange={(e) => setSelectedSubtopic(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  disabled={!selectedModule}
                  data-testid="subtopic-select"
                >
                  <option value="">Selecciona un subtema</option>
                  {subtopics.map((subtopic) => (
                    <option key={subtopic.id} value={subtopic.id}>
                      {subtopic.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Frequency Select */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Frecuencia de notificaciones
                </label>
                <select
                  value={intervalMinutes}
                  onChange={(e) => setIntervalMinutes(Number(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  data-testid="interval-select"
                >
                  <option value={15}>Cada 15 minutos (intensivo)</option>
                  <option value={30}>Cada 30 minutos</option>
                  <option value={60}>Cada 60 minutos (recomendado)</option>
                  <option value={120}>Cada 2 horas</option>
                </select>
                {intervalMinutes === 15 && (
                  <p className="text-amber-600 text-sm mt-1">
                    ⚠️ Notificaciones cada 15 minutos puede ser muy frecuente. Considera empezar con cada hora.
                  </p>
                )}
              </div>

              {/* Time Range */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Clock size={14} className="inline mr-1" />
                    Desde
                  </label>
                  <input
                    type="time"
                    value={startHour}
                    onChange={(e) => setStartHour(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500"
                    data-testid="start-hour"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hasta
                  </label>
                  <input
                    type="time"
                    value={endHour}
                    onChange={(e) => setEndHour(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500"
                    data-testid="end-hour"
                  />
                </div>
              </div>

              {/* Timezone */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Globe size={14} className="inline mr-1" />
                  Zona horaria
                </label>
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500"
                  data-testid="timezone-select"
                >
                  <option value="America/Mexico_City">Ciudad de México (GMT-6)</option>
                  <option value="America/Tijuana">Tijuana (GMT-8)</option>
                  <option value="America/Cancun">Cancún (GMT-5)</option>
                  <option value="America/Chihuahua">Chihuahua (GMT-7)</option>
                </select>
              </div>

              {/* Action Buttons */}
              {!currentPreference ? (
                <button
                  onClick={handleActivate}
                  className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2"
                  data-testid="activate-button"
                >
                  <Bell size={20} />
                  Activar notificaciones
                </button>
              ) : (
                <div className="space-y-3">
                  {currentPreference.is_paused ? (
                    <button
                      onClick={handleResume}
                      className="w-full py-4 bg-green-500 text-white font-semibold rounded-xl hover:bg-green-600 transition-all flex items-center justify-center gap-2"
                      data-testid="resume-button"
                    >
                      <Play size={20} />
                      Reanudar notificaciones
                    </button>
                  ) : (
                    <button
                      onClick={handlePause}
                      className="w-full py-4 bg-yellow-500 text-white font-semibold rounded-xl hover:bg-yellow-600 transition-all flex items-center justify-center gap-2"
                      data-testid="pause-button"
                    >
                      <Pause size={20} />
                      Pausar notificaciones
                    </button>
                  )}
                  
                  <button
                    onClick={handleUnsubscribe}
                    className="w-full py-3 border border-red-300 text-red-600 font-medium rounded-xl hover:bg-red-50 transition-all flex items-center justify-center gap-2"
                    data-testid="unsubscribe-button"
                  >
                    <BellOff size={18} />
                    Cancelar suscripción
                  </button>
                </div>
              )}
            </div>

            {/* Current Subscription Info */}
            {currentPreference && (
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Tu suscripción actual
                </h3>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">Módulo</span>
                    <span className="font-medium text-gray-800">
                      {currentPreference.module?.name || '-'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">Subtema</span>
                    <span className="font-medium text-gray-800">
                      {currentPreference.subtopic?.name || '-'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">Frecuencia</span>
                    <span className="font-medium text-gray-800">
                      Cada {currentPreference.interval_minutes || 60} minutos
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600">Horario</span>
                    <span className="font-medium text-gray-800">
                      {currentPreference.start_hour} - {currentPreference.end_hour}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PerlasDiarias;
